import { Condition } from '@api/api/compass-schema';
import { z } from 'zod';

import { MappedConditions } from '../use-scenario-assistant/enrichedConditions';

const conditionSchema = z.discriminatedUnion('condition', [
  z.object({
    condition: z.literal('crosses_above'),
    value: z.number(),
  }),
  z.object({
    condition: z.literal('crosses_below'),
    value: z.number(),
  }),
  z.object({
    condition: z.literal('above'),
    value: z.number(),
  }),
  z.object({
    condition: z.literal('below'),
    value: z.number(),
  }),
  z.object({
    condition: z.literal('between'),
    max_value: z.number(),
    min_value: z.number(),
  }),
  z.object({
    condition: z.literal('rises_by'),
    value: z.number(),
    duration: z.number(),
    duration_unit: z.enum(['m', 'd', 'w', 'y']),
  }),
  z.object({
    condition: z.literal('falls_by'),
    value: z.number(),
    duration: z.number(),
    duration_unit: z.enum(['m', 'd', 'w', 'y']),
  }),
  z.object({
    condition: z.literal('rises_by_level'),
    value: z.number(),
    duration: z.number(),
    duration_unit: z.enum(['m', 'd', 'w', 'y']),
  }),
  z.object({
    condition: z.literal('falls_by_level'),
    value: z.number(),
    duration: z.number(),
    duration_unit: z.enum(['m', 'd', 'w', 'y']),
  }),
]);

export const validateCondition = <T extends Condition>(
  condition: Partial<T>,
  fullCondition?: boolean
) => {
  const withEntityAndSnake = fullCondition
    ? !!(condition.entity && condition.snake)
    : true;
  return withEntityAndSnake && conditionSchema.safeParse(condition).success;
};

export const validateConditions = <T extends Condition>(
  conditions: Partial<T>[]
) => {
  return conditions.every(c => validateCondition(c, true));
};

export const normalizeMinMaxValues = <T extends MappedConditions>(
  condition: Partial<T>
) => {
  const c = { ...condition };

  if (c.min_value !== undefined && c.max_value !== undefined) {
    if (c.min_value > c.max_value) {
      const temp = c.max_value;
      c.max_value = c.min_value;
      c.min_value = temp;
    }
  }

  return c;
};
